class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.defaultSearch = this.querySelector(".default--search");
    this.demoSearch = this.querySelector(".wbsclose");

    this.selectedCategory = '';
    this.setupCategoryListeners();

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.querySelector(".wbsclose")?.addEventListener('click', this.onClick.bind(this));
  }

  setupCategoryListeners() {
    const select = document.getElementById('SearchFilter');
    if (!select) return;

    this.selectedCategory = select.value || '';

    select.addEventListener('change', (e) => {
      this.setSelectedCategory(e.target.value);
    });
  }

  setSelectedCategory(category) {
    this.selectedCategory = category;

    const hiddenInput = document.querySelector('#predictive-search-category');
    if (hiddenInput) hiddenInput.value = category;

    if (this.getQuery().length) {
      this.getSearchResults(this.getQuery());
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.defaultSearch.classList.contains('hidden')) {
      this.defaultSearch.classList.add("hidden");
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.defaultSearch.classList.remove("hidden");
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Use Shopify native product type filter to avoid polluting the visible query
    const normalizedCategory = this.selectedCategory?.trim().toLowerCase();
    const noCategory = !normalizedCategory
      || normalizedCategory === 'all categories'
      || normalizedCategory === 'semua kategori';

    if (!noCategory) {
      event.preventDefault();
      const params = new URLSearchParams(new FormData(this.input.form));
      const cleanQuery = this.getQuery();
      // Append category to q for Shopify search filtering, keep display_q for clean input display
      params.set('q', `${cleanQuery} ${this.selectedCategory}`);
      params.set('display_q', cleanQuery);
      params.set('SearchFilter', this.selectedCategory);
      params.delete('category');
      params.delete('filter.p.product_type');
      window.location.href = `${this.input.form.action}?${params.toString()}`;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    this.defaultSearch.classList.remove("hidden");
    this.demoSearch.classList.remove("hidden");

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.defaultSearch.classList.add("hidden");
        this.demoSearch.classList.add("hidden");
        this.close();
      }
    }, 100);
  }

  onClick() {
    setTimeout(() => {
      this.defaultSearch.classList.add("hidden");
      this.demoSearch.classList.add("hidden");
      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter(el => el.offsetParent !== null);

    let activeIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedIndex = -1;
    allVisibleElements.forEach((el, i) => {
      if (el === selectedElement) selectedIndex = i;
    });

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeIndex = selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
    } else if (moveUp) {
      activeIndex = selectedIndex === 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
    }

    if (activeIndex === selectedIndex) return;

    const activeElement = allVisibleElements[activeIndex];
    activeElement.setAttribute('aria-selected', true);
    selectedElement?.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    selectedOption?.click();
  }

  getSearchResults(searchTerm) {
    const normalizedCategory = this.selectedCategory?.trim().toLowerCase();
    const queryKey = (searchTerm + (normalizedCategory || ''))
      .replace(/\s+/g, '-')
      .toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const query =
      normalizedCategory
        ? `${searchTerm} ${normalizedCategory}`
        : searchTerm;

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=predictive-search`, {
      signal: this.abortController.signal
    })
      .then(response => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch(error => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || ''
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    selected?.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);